import React, { useRef, useEffect } from 'react';
import { useResizeObserver } from 'usehooks-ts';
import HeroTitle from './HeroTitle';
import HeroLeadGraphic from './HeroLeadGraphic';
import HeroCTA from './HeroCTA';
import { useHeroConfig } from '../../stores/index.js';
import { getCardStyle } from './heroUtils';
import styles from './HeroOverlay.module.scss';

const MeasurableOverlayGroup = ({ id, onHeightChange, children, className, style }) => {
    const ref = useRef(null);
    const { height = 0 } = useResizeObserver({
        ref,
        box: 'border-box',
    });

    useEffect(() => {
        onHeightChange(id, height);
        return () => {
            onHeightChange(id, 0);
        };
    }, [id, height, onHeightChange]);

    return (
        <div ref={ref} className={className} style={style}>
            {children}
        </div>
    );
};

/**
 * Default spacing of the top/bottom overlay groups: 16px on mobile so they do not sit flush
 * against the key visual edge. Campaigns whose key visual always had a flush mobile overlay
 * pass their own `padding` instead.
 */
const DEFAULT_PADDING = { top: 'pt-3 pt-md-5', bottom: 'pb-3 pb-md-5' };

const HeroOverlay = ({ onGroupHeightChange, padding = DEFAULT_PADDING }) => {
    const heroConfig = useHeroConfig();
    const isSplit =
        heroConfig.background === 'keyVisual' &&
        (heroConfig.keyVisualSplit === 'split-left' || heroConfig.keyVisualSplit === 'split-right');

    const activeElements = [];
    if (heroConfig.title?.hasTitle) {
        activeElements.push({
            id: 'title',
            horizontalPosition: heroConfig.title.horizontalPosition || 'left',
            verticalPosition: heroConfig.title.verticalPosition || 'top',
            render: () => <HeroTitle titleConfig={heroConfig.title} />,
        });
    }
    if (heroConfig.leadGraphic?.hasLeadGraphic) {
        activeElements.push({
            id: 'leadGraphic',
            horizontalPosition: heroConfig.leadGraphic.horizontalPosition || 'left',
            verticalPosition: heroConfig.leadGraphic.verticalPosition || 'top',
            render: () => <HeroLeadGraphic leadGraphicConfig={heroConfig.leadGraphic} />,
        });
    }
    if (heroConfig.cta?.hasCTA) {
        activeElements.push({
            id: 'cta',
            horizontalPosition: heroConfig.cta.horizontalPosition || 'left',
            verticalPosition: heroConfig.cta.verticalPosition || 'top',
            render: () => <HeroCTA ctaConfig={heroConfig.cta} />,
        });
    }

    const positionGroups = {};
    activeElements.forEach((el) => {
        const key = `${el.horizontalPosition}-${el.verticalPosition}`;
        if (!positionGroups[key]) {
            positionGroups[key] = {
                horizontalPosition: el.horizontalPosition,
                verticalPosition: el.verticalPosition,
                elements: [],
            };
        }
        positionGroups[key].elements.push(el);
    });

    const getPositionClasses = (horizontal, vertical) => {
        let hClass = 'start-0 text-start';
        if (horizontal === 'center') hClass = 'start-50 translate-middle-x text-center';
        if (horizontal === 'right') hClass = 'end-0 text-end';

        // Vertical spacing of the top/bottom groups comes from the product policy.
        let vClass = `top-0 ${padding.top}`;
        if (vertical === 'center') vClass = 'top-50 translate-middle-y';
        if (vertical === 'bottom') vClass = `bottom-0 ${padding.bottom}`;

        // For center-center position, we need both translate-middle-x and translate-middle-y, which is translate-middle!
        if (horizontal === 'center' && vertical === 'center') {
            return 'position-absolute start-50 top-50 translate-middle text-center';
        }

        return `position-absolute ${hClass} ${vClass}`;
    };

    const getAlignmentClass = (horizontal) => {
        if (horizontal === 'center') return styles.alignCenter;
        if (horizontal === 'right') return styles.alignRight;
        return styles.alignLeft;
    };

    return (
        <>
            {Object.entries(positionGroups).map(([positionKey, group]) => {
                const hasMultiple = group.elements.length > 1;
                const mainEl = group.elements[0];
                return (
                    <div
                        key={positionKey}
                        className={`position-absolute top-0 start-50 translate-middle-x h-100 ${styles.titleOverlayContainer} container-lg px-0 ${
                            isSplit ? 'd-none d-md-block' : ''
                        }`}
                    >
                        <div className="position-relative w-100 h-100">
                            <MeasurableOverlayGroup
                                id={positionKey}
                                onHeightChange={onGroupHeightChange}
                                className={`${getPositionClasses(
                                    group.horizontalPosition,
                                    group.verticalPosition
                                )} ${isSplit ? '' : 'px-3'} ${
                                    hasMultiple
                                        ? `${styles.stackedOverlayContainer} ${getAlignmentClass(group.horizontalPosition)}`
                                        : mainEl.id === 'title' || mainEl.id === 'leadGraphic'
                                          ? `text-white ${styles.pointerEventsNone}`
                                          : styles.pointerEventsAuto
                                } ${isSplit ? styles.splitPositionedContainer : ''}`}
                            >
                                {group.elements.map((el) => {
                                    if (el.id === 'cta') {
                                        return (
                                            <div
                                                key={el.id}
                                                className={
                                                    heroConfig.cta?.card?.hasNoCard
                                                        ? styles.ctaNoCard
                                                        : `card text-start ${styles.ctaCard}`
                                                }
                                                style={
                                                    heroConfig.cta?.card?.hasNoCard
                                                        ? { pointerEvents: 'auto' }
                                                        : {
                                                              pointerEvents: 'auto',
                                                              ...getCardStyle(heroConfig.cta),
                                                          }
                                                }
                                            >
                                                {el.render()}
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={el.id} className={styles.pointerEventsNone}>
                                            {el.render()}
                                        </div>
                                    );
                                })}
                            </MeasurableOverlayGroup>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export default HeroOverlay;
